import {
  ArrowRight,
  Archive,
  BookOpen,
  CheckCircle2,
  Cpu,
  Download,
  ExternalLink,
  Gauge,
  Github,
  HardDrive,
  Image,
  Layers,
  Lock,
  PackageCheck,
  Play,
  Server,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Terminal,
  Zap
} from "lucide-react";

import { siteConfig } from "./config/site";

type Step = {
  title: string;
  body: string;
  command?: string;
};

type Requirement = {
  label: string;
  value: string;
  note: string;
};

const quickStart: Step[] = [
  {
    title: "Clone the project",
    body: "Grab ChitraDev from GitHub and open the folder locally.",
    command: "git clone https://github.com/yuvrajraina/ChitraDev.git"
  },
  {
    title: "Create the environment",
    body: "Use the included setup script to create a Python 3.11 virtual environment and install the CUDA PyTorch stack.",
    command: "powershell -ExecutionPolicy Bypass -File .\\scripts\\setup.ps1"
  },
  {
    title: "Preload the models",
    body: "Warm the Stable Diffusion, IP-Adapter FaceID, and InsightFace assets before opening the app.",
    command: "powershell -ExecutionPolicy Bypass -File .\\scripts\\preload_models.ps1"
  },
  {
    title: "Run locally",
    body: "Start the ChitraDev FastAPI server and open the local browser UI.",
    command: "python -m uvicorn app.main:app --host 127.0.0.1 --port 8000"
  }
];

const requirements: Requirement[] = [
  {
    label: "Python",
    value: "3.11",
    note: "The setup script creates a Python 3.11 virtual environment."
  },
  {
    label: "GPU",
    value: "NVIDIA CUDA recommended",
    note: "Defaults are tuned for a 4 GB GPU at 512x512, 20 steps, 1 output."
  },
  {
    label: "Disk",
    value: "Several GB free",
    note: "Base model, IP-Adapter weights, and InsightFace assets cache locally."
  },
  {
    label: "OS",
    value: "Windows-first scripts",
    note: "PowerShell setup scripts are included; Linux/macOS can adapt the Python commands manually."
  },
  {
    label: "Node",
    value: "20+ for website",
    note: "Only needed when running or building the public React website."
  }
];

const modes = [
  {
    icon: Sparkles,
    title: "Prompt-only generation",
    body: "Leave face uploads empty and paste the full image brief, including structured logo and brand prompts."
  },
  {
    icon: Image,
    title: "Single-person FaceID",
    body: "Upload Person 1 and add an optional role or action prompt for identity-guided portraits."
  },
  {
    icon: ShieldCheck,
    title: "Two-person FaceID",
    body: "Upload two identities, assign left/right placement, and describe how they interact."
  },
  {
    icon: Download,
    title: "Batch creator",
    body: "Open a separate local window for long prompt-only runs that produce dozens or hundreds of outputs."
  }
];

const batchHighlights = [
  {
    icon: Layers,
    title: "Hundreds of outputs",
    text: "Reuse one prompt across a long-running local job without keeping the standard request open."
  },
  {
    icon: SlidersHorizontal,
    title: "Own batch settings",
    text: "Tune total outputs, chunk size, dimensions, steps, guidance, negative prompt, and start seed separately."
  },
  {
    icon: Gauge,
    title: "Hardware-aware chunks",
    text: "Keep chunk size low for smaller GPUs, or raise it when your workstation can comfortably handle more."
  },
  {
    icon: PackageCheck,
    title: "ZIP at the end",
    text: "Every batch folder includes generated PNGs, the prompt summary, and a downloadable results archive."
  }
];

const batchFlow = [
  "Start ChitraDev locally.",
  "Open /batch in a separate browser window.",
  "Paste the reusable prompt and choose batch-only settings.",
  "Watch progress, preview recent images, cancel if needed, then download the ZIP."
];

const generationNotes = [
  "No hosted image-generation quotas, remote credits, or subscription caps are added by ChitraDev.",
  "No repo-level prompt category allowlist or denylist is imposed by the app itself.",
  "Generation volume is controlled by your own machine, model cache, GPU memory, and .env settings.",
  "First run can take time while Stable Diffusion, IP-Adapter FaceID, and InsightFace assets download locally.",
  "CPU generation is possible, but CUDA is strongly recommended for a smooth local workflow.",
  "FaceID quality depends on clear reference photos, strong prompts, and realistic hardware settings."
];

const docs = [
  {
    title: "Outputs stay local",
    text: "Generated PNGs and ZIP files are written into the ignored outputs folder. They are not meant to be committed."
  },
  {
    title: "No hosted generation caps",
    text: "ChitraDev does not meter generations with credits, subscriptions, or hosted usage quotas."
  },
  {
    title: "Open prompt surface",
    text: "The repo does not add creative category blocks; practical generation behavior comes from your model, hardware, and local settings."
  },
  {
    title: "Structured logo briefs",
    text: "Paste sections like Style, Colors, Typography, Icon idea, Avoid, and Output; ChitraDev compacts them before generation."
  },
  {
    title: "Separate batch window",
    text: "Use /batch for long prompt-only jobs with total output count, chunk size, dimensions, steps, guidance, seed, progress, cancel, and ZIP download."
  },
  {
    title: "Config is environment-based",
    text: "Use .env for local model paths, cache paths, host/port, default image size, and hardware-friendly request caps."
  },
  {
    title: "Validation is server-side",
    text: "The API validates dimensions, steps, guidance, seeds, upload types, and FaceID control combinations."
  },
  {
    title: "Health endpoint included",
    text: "Use /health to verify the service is alive and whether the model runtime has loaded."
  }
];

function App() {
  return (
    <div className="site-shell">
      <Header />
      <main>
        <Hero />
        <FeatureStrip />
        <BulkProduction />
        <QuickStart />
        <Docs />
        <Requirements />
        <NoLimits />
        <Readme />
      </main>
      <Footer />
    </div>
  );
}

function Header() {
  return (
    <header className="topbar">
      <a className="brand" href="#top" aria-label="ChitraDev home">
        <span className="brand-mark">
          <Image size={18} aria-hidden="true" />
        </span>
        <span>{siteConfig.name}</span>
      </a>
      <nav className="nav-links" aria-label="Primary navigation">
        <a href="#docs">Docs</a>
        <a href="#bulk">Bulk</a>
        <a href="#requirements">Requirements</a>
        <a href="#no-limits">No limits</a>
        <a href="#readme">README</a>
      </nav>
      <a className="icon-button" href={siteConfig.githubUrl} target="_blank" rel="noreferrer">
        <Github size={18} aria-hidden="true" />
        <span>GitHub</span>
      </a>
    </header>
  );
}

function Hero() {
  return (
    <section
      id="top"
      className="hero"
      style={{ backgroundImage: `linear-gradient(90deg, rgba(8, 11, 16, 0.88), rgba(8, 11, 16, 0.52)), url("${siteConfig.heroImage}")` }}
    >
      <div className="hero-content">
        <p className="eyebrow">
          <Lock size={16} aria-hidden="true" />
          Local-first Stable Diffusion
        </p>
        <h1>ChitraDev runs image generation from your own machine.</h1>
        <p className="hero-copy">
          {siteConfig.description} Keep generated images, model cache, and reference photos local while sharing a clean open-source project with the community.
        </p>
        <div className="hero-actions">
          <a className="primary-action" href={siteConfig.githubUrl} target="_blank" rel="noreferrer">
            <Github size={20} aria-hidden="true" />
            Open GitHub repo
          </a>
          <a className="secondary-action" href="#quick-start">
            <Terminal size={20} aria-hidden="true" />
            Quick start
          </a>
          <a className="secondary-action" href="#bulk">
            <Archive size={20} aria-hidden="true" />
            Bulk production
          </a>
        </div>
      </div>
      <div className="status-panel" aria-label="Project status summary">
        <div>
          <span className="metric">No quotas</span>
          <span className="metric-label">local generation</span>
        </div>
        <div>
          <span className="metric">FastAPI</span>
          <span className="metric-label">local backend</span>
        </div>
        <div>
          <span className="metric">Batch</span>
          <span className="metric-label">hundreds of outputs</span>
        </div>
      </div>
    </section>
  );
}

function FeatureStrip() {
  return (
    <section className="section feature-strip" aria-label="Generation modes">
      <div className="section-heading">
        <p className="eyebrow dark">
          <Zap size={16} aria-hidden="true" />
          What it does
        </p>
        <h2>One local app, multiple practical workflows.</h2>
      </div>
      <div className="mode-grid">
        {modes.map((mode) => {
          const Icon = mode.icon;
          return (
            <article className="mode-card" key={mode.title}>
              <Icon size={24} aria-hidden="true" />
              <h3>{mode.title}</h3>
              <p>{mode.body}</p>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function BulkProduction() {
  return (
    <section id="bulk" className="section bulk-section">
      <div className="section-heading">
        <p className="eyebrow dark">
          <Archive size={16} aria-hidden="true" />
          Bulk production
        </p>
        <h2>Run long prompt-only batches in their own window.</h2>
        <p>
          ChitraDev includes a separate batch creator for production runs that need dozens or hundreds of outputs from the same prompt.
        </p>
      </div>

      <div className="bulk-layout">
        <div className="bulk-panel" aria-label="Batch creator setting preview">
          <div className="bulk-panel-top">
            <span>Batch creator</span>
            <strong>/batch</strong>
          </div>
          <div className="batch-meter">
            <span style={{ width: "72%" }} />
          </div>
          <div className="bulk-stats">
            <div>
              <strong>100</strong>
              <span>default outputs</span>
            </div>
            <div>
              <strong>500</strong>
              <span>default max cap</span>
            </div>
            <div>
              <strong>ZIP</strong>
              <span>final archive</span>
            </div>
          </div>
          <div className="setting-list">
            <span>Total outputs</span>
            <span>Images per chunk</span>
            <span>Width and height</span>
            <span>Steps and guidance</span>
            <span>Negative prompt</span>
            <span>Start seed</span>
          </div>
        </div>

        <div className="bulk-copy">
          <div className="bulk-highlight-grid">
            {batchHighlights.map((item) => {
              const Icon = item.icon;
              return (
                <article className="bulk-highlight" key={item.title}>
                  <Icon size={22} aria-hidden="true" />
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              );
            })}
          </div>
          <ol className="batch-flow">
            {batchFlow.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

function QuickStart() {
  return (
    <section id="quick-start" className="section split-section">
      <div className="section-heading sticky-heading">
        <p className="eyebrow dark">
          <Download size={16} aria-hidden="true" />
          How to use
        </p>
        <h2>Clone, install, preload, run.</h2>
        <p>
          ChitraDev is designed for local use. Generated files go into ignored folders, and the browser UI talks to a local FastAPI server at 127.0.0.1.
        </p>
      </div>
      <div className="steps">
        {quickStart.map((step, index) => (
          <article className="step" key={step.title}>
            <span className="step-number">{index + 1}</span>
            <div>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
              {step.command ? <code>{step.command}</code> : null}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function Docs() {
  return (
    <section id="docs" className="section docs-section">
      <div className="section-heading">
        <p className="eyebrow dark">
          <BookOpen size={16} aria-hidden="true" />
          Docs
        </p>
        <h2>Implementation notes for people evaluating the repo.</h2>
      </div>
      <div className="docs-grid">
        {docs.map((item) => (
          <article className="doc-item" key={item.title}>
            <CheckCircle2 size={22} aria-hidden="true" />
            <div>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function Requirements() {
  return (
    <section id="requirements" className="section requirements-section">
      <div className="section-heading">
        <p className="eyebrow dark">
          <Cpu size={16} aria-hidden="true" />
          System requirements
        </p>
        <h2>Designed for local GPU workstations.</h2>
      </div>
      <div className="requirement-list">
        {requirements.map((item) => (
          <article className="requirement" key={item.label}>
            <span>{item.label}</span>
            <strong>{item.value}</strong>
            <p>{item.note}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function NoLimits() {
  return (
    <section id="no-limits" className="section no-limits-section">
      <div className="section-heading">
        <p className="eyebrow dark">
          <Sparkles size={16} aria-hidden="true" />
          No hosted limits
        </p>
        <h2>ChitraDev is not a credit-metered hosted generator.</h2>
        <p>
          The repo does not add generation quotas or prompt-category blocks. Your local hardware, model choice, and environment settings decide what is practical to generate.
        </p>
      </div>
      <ul className="generation-note-list">
        {generationNotes.map((item) => (
          <li key={item}>
            <CheckCircle2 size={18} aria-hidden="true" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Readme() {
  return (
    <section id="readme" className="section readme-section">
      <div className="readme-copy">
        <p className="eyebrow dark">
          <Server size={16} aria-hidden="true" />
          README
        </p>
        <h2>Everything routes back to the source repo.</h2>
        <p>
          The GitHub README is the canonical setup document. This website is the public front door: concise overview, easy local installation path, requirements, no-quota generation notes, and links back to the code.
        </p>
        <div className="readme-actions">
          <a className="primary-action light" href={siteConfig.githubUrl} target="_blank" rel="noreferrer">
            <Github size={20} aria-hidden="true" />
            View repository
          </a>
          <a className="secondary-action light" href={siteConfig.frontendGithubUrl} target="_blank" rel="noreferrer">
            <ExternalLink size={20} aria-hidden="true" />
            Website repo
          </a>
          <a className="secondary-action light" href={`${siteConfig.githubUrl}#readme`} target="_blank" rel="noreferrer">
            <ExternalLink size={20} aria-hidden="true" />
            Open README
          </a>
        </div>
      </div>
      <div className="terminal-card" aria-label="README command summary">
        <div className="terminal-top">
          <span />
          <span />
          <span />
        </div>
        <pre>{`# ChitraDev
Copy-Item .env.example .env
powershell -ExecutionPolicy Bypass -File .\\scripts\\setup.ps1
.\\.venv\\Scripts\\Activate.ps1
python -m uvicorn app.main:app --host 127.0.0.1 --port 8000
# Standard UI: http://127.0.0.1:8000
# Batch UI:    http://127.0.0.1:8000/batch`}</pre>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div>
        <strong>{siteConfig.name}</strong>
        <p>{siteConfig.tagline}</p>
      </div>
      <div className="footer-links">
        <a href={siteConfig.githubUrl} target="_blank" rel="noreferrer">
          <Github size={18} aria-hidden="true" />
          GitHub
        </a>
        <a href="#requirements">
          <HardDrive size={18} aria-hidden="true" />
          Requirements
        </a>
        <a href="#quick-start">
          <ArrowRight size={18} aria-hidden="true" />
          Start
        </a>
      </div>
    </footer>
  );
}

export default App;
